import { DateTime } from 'luxon';
import type { SagaIterator } from 'redux-saga';
import { call, select } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { getLogger } from '../../utils/core';
import { authSelectors } from '../auth';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { CoreAction } from '../core/resourceStoreTypes';
import * as actions from './achievementsActions';
import type { AchievementEventPayload, AchievementEventProps, AchievementProps } from './achievementsTypes';

const log = getLogger('data/achievements/achievementsSagas.ts');

const mkQcsSyncAchievementsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<AchievementProps>();
const mkQcsSyncAchievementEventConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<AchievementEventProps>();

export function* getAchievementsActionWatcher() {
  const config = mkQcsSyncAchievementsConfig({
    resourceName: 'achievements',
    resourceBaseUrl: '/gamification/achievements',
    successAction: actions.getAchievementsSuccess,
    failureAction: actions.getAchievementsFailure,
  });
  yield resourceSagaUtils.takeAndDebounce(
    actions.getAchievements,
    resourceSagaUtils.qcsSyncGetResources<AchievementProps>,
    config,
  );
}

export function* getEventsActionWatcher() {
  yield resourceSagaUtils.takeAndDebounce(actions.sendAchievementsEvent, callAchievementsEvent);
}

function* callAchievementsEvent(action: CoreAction<AchievementEventPayload, any>): SagaIterator {
  // Gamification events are dataset scoped; without a selected dataset the request is rejected client side (SIMPL-35607).
  if (!(yield select(authSelectors.getDatasetId))) {
    log.info('gamification event skipped, no dataset selected', action.payload);
    return;
  }
  const date = DateTime.local().toISODate();
  const config = mkQcsSyncAchievementEventConfig({
    resourceName: 'achievements',
    resourceBaseUrl: '/gamification/event',
    transformResourceToRequestData: (resource) => ({
      ...(resource as AchievementEventProps),
      eventId: uuid(),
      eventOn: date,
    }),
    successAction: actions.sendAchievementsEventSuccess,
    failureAction: actions.sendAchievementsEventFailure,
    transformResourceWithResponse: () => null,
    handleRetry: false,
    ignoreError: true,
  });
  try {
    yield call(resourceSagaUtils.qcsSyncCreateResource<AchievementEventProps>, config, action);
  } catch (error) {
    log.error('gamification event error ', error);
  }
}

export default [getAchievementsActionWatcher, getEventsActionWatcher];
